import React, { useEffect, useState } from 'react';
import { istDateTime } from '../lib/ist';
import axios from 'axios';
import { RefreshCw, Search } from 'lucide-react';
import { API, useAuth } from '../App';

const AuditLog = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ actor: '', action: '', target_type: '' });
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 100, page };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/audit-log`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLogs(); }, [page]);

  const apply = () => { setPage(1); fetchLogs(); };

  const actionColor = (a) => {
    if (a.startsWith('voucher.')) return 'bg-blue-50 text-blue-700';
    if (a.startsWith('kiosk.')) return 'bg-purple-50 text-purple-700';
    if (a.startsWith('order.')) return 'bg-green-50 text-green-700';
    if (a.startsWith('admin.')) return 'bg-amber-50 text-amber-800';
    if (a.startsWith('system.')) return 'bg-red-50 text-red-700';
    if (a.startsWith('auth.')) return 'bg-gray-100 text-gray-700';
    return 'bg-gray-50 text-gray-600';
  };

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div>
        <h1 className="font-serif text-3xl text-charcoal-900">Audit Log</h1>
        <p className="text-sm text-charcoal-800/60 mt-1">Every admin mutation across the platform — {total} total entries</p>
      </div>

      <div className="card-dashboard flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[120px]">
          <label className="text-xs text-charcoal-800/60">Actor</label>
          <input value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} placeholder="username" className="w-full px-3 py-1.5 border border-charcoal-900/10 rounded-lg text-sm" />
        </div>
        <div className="flex-1 min-w-[120px]">
          <label className="text-xs text-charcoal-800/60">Action</label>
          <input value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} placeholder="kiosk.reboot" className="w-full px-3 py-1.5 border border-charcoal-900/10 rounded-lg text-sm font-mono" />
        </div>
        <div className="flex-1 min-w-[120px]">
          <label className="text-xs text-charcoal-800/60">Target type</label>
          <input value={filters.target_type} onChange={(e) => setFilters({ ...filters, target_type: e.target.value })} placeholder="kiosk" className="w-full px-3 py-1.5 border border-charcoal-900/10 rounded-lg text-sm" />
        </div>
        <button onClick={apply} className="bg-charcoal-900 text-cream-50 px-4 py-1.5 rounded-lg text-sm flex items-center gap-1">
          <Search className="w-3.5 h-3.5" /> Apply
        </button>
        <button onClick={fetchLogs} className="border border-charcoal-900/10 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="card-dashboard p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-charcoal-800/40">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-charcoal-800/40">No entries match the filters</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead className="bg-cream-50">
                <tr className="text-left">
                  <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase w-44">Time</th>
                  <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase">Actor</th>
                  <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase">Action</th>
                  <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase">Target</th>
                  <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-900/5">
                {logs.map(log => {
                  const isOpen = expandedId === log.id;
                  const hasDetails = log.before || log.after || log.extra;
                  return (
                    <React.Fragment key={log.id}>
                      <tr onClick={() => hasDetails && setExpandedId(isOpen ? null : log.id)} className={`hover:bg-cream-50 ${hasDetails ? 'cursor-pointer' : ''}`}>
                        <td className="px-3 py-2 text-xs text-charcoal-800/60 font-mono whitespace-nowrap">
                          {istDateTime(log.ts)}
                        </td>
                        <td className="px-3 py-2 text-charcoal-900">
                          <div>{log.actor_username}</div>
                          <div className="text-[10px] text-charcoal-800/40">{log.actor_role}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[11px] font-mono px-2 py-0.5 rounded ${actionColor(log.action)}`}>{log.action}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-charcoal-800/70">
                          {log.target_type}{log.target_id && <div className="font-mono text-[10px] text-charcoal-800/50">{log.target_id.slice(0, 8)}…</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-charcoal-800/50 font-mono">{log.ip || '—'}</td>
                      </tr>
                      {isOpen && hasDetails && (
                        <tr className="bg-charcoal-900 text-cream-50">
                          <td colSpan={5} className="px-3 py-3">
                            <pre className="text-[11px] font-mono whitespace-pre-wrap break-words max-h-72 overflow-auto">
                              {JSON.stringify({ before: log.before, after: log.after, extra: log.extra }, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-3 py-2 border-t border-charcoal-900/10 bg-cream-50 flex items-center justify-between text-xs text-charcoal-800/60">
          <span>Page {page} · showing {logs.length} of {total}</span>
          <div className="flex gap-1">
            <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 border border-charcoal-900/10 rounded disabled:opacity-30">Prev</button>
            <button disabled={logs.length < 100} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border border-charcoal-900/10 rounded disabled:opacity-30">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
